import { useState, useEffect } from "react";
import { Form, Row, Col, Input, Image, Typography, Button, Checkbox, Tooltip, message, Progress } from "antd";
import { useTranslation } from "react-i18next";
import { TFunction } from 'types';
import { useLoginForm } from "./useLoginForm";
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { cookies } from "static";
import { ApiError, ERROR_TYPES } from "api/errorTypes";
import { getLoginRateLimitMessage } from "api/loginMessages";
import { clearAnalyticsFarmContext, trackLoginSuccess } from 'analytics/ga4';
import i18n from '../../i18n';
import { clearAuthSession } from 'utils/clearAuthSession';

const getStoredAuth = () => {
  const storedAuth = localStorage.getItem(cookies.FarmersPlanToken);

  if (!storedAuth) {
    return null;
  }

  try {
    return JSON.parse(storedAuth);
  } catch {
    localStorage.removeItem(cookies.FarmersPlanToken);
    return null;
  }
};

export default function RegisterForm () {

  const {
    isLoggingIn,
    login,
    loginData,
    hasLoggedIn,
    failedLoggingIn,
    loginError,
    isCheckingCrops,
    checkCropsData,
    checkCrops,
    hasCheckedCrops,
    failedCheckingCrops,
    checkCropsError,
  } = useLoginForm();

  const [searchParams] = useSearchParams();
  const completeRegistration = searchParams.get("completeRegistration");
  const [loginDisabled, setLoginDisabled] = useState(false);
  const [sessionCheckProgress, setSessionCheckProgress] = useState(12);
  const storedAuth = getStoredAuth();
  const storedToken = storedAuth?.token || null;
  const storedUsername = storedAuth?.username || '';
  const isExistingSessionCheckPending = Boolean(storedToken) && !failedCheckingCrops;
  
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const { t }: TFunction = useTranslation();
  const validatedEmail = typeof location.state?.email === 'string' ? location.state.email.trim() : '';
  const cameFromEmailValidation = location.state?.emailValidated === true;

  useEffect(() => {
    if (!cameFromEmailValidation) {
      return;
    }

    if (validatedEmail) {
      form.setFieldsValue({ username: validatedEmail });
    }

    message.success({
      content: t('EmailValidatedPleaseLogin'),
      duration: 5,
    });
    navigate(location.pathname + location.search, { replace: true, state: {} });
  }, [cameFromEmailValidation, form, location.pathname, location.search, navigate, t, validatedEmail]);

  useEffect(() => {
    if (!storedToken || hasCheckedCrops || failedCheckingCrops || isCheckingCrops) {
      return;
    }

    checkCrops();
  }, [storedToken, hasCheckedCrops, failedCheckingCrops, isCheckingCrops, checkCrops]);

  useEffect(() => {
    const isUnauthorized = checkCropsError instanceof ApiError && checkCropsError.statusCode === 401;

    if (!failedCheckingCrops || !isUnauthorized) {
      return;
    }

    clearAuthSession();
  }, [failedCheckingCrops, checkCropsError]);

  useEffect(() => {
    if (!isExistingSessionCheckPending) {
      setSessionCheckProgress(12);
      return;
    }

    setSessionCheckProgress(18);

    const interval = window.setInterval(() => {
      setSessionCheckProgress((value) => {
        if (value >= 92) {
          return value;
        }

        if (value < 60) {
          return Math.min(92, value + 8);
        }

        if (value < 80) {
          return Math.min(92, value + 4);
        }

        return Math.min(92, value + 2);
      });
    }, 220);

    return () => {
      window.clearInterval(interval);
    };
  }, [isExistingSessionCheckPending]);

  useEffect(() => {
    const token = storedToken;

    if (token && isCheckingCrops) {

      setLoginDisabled(true);

    } 

    if (hasCheckedCrops || failedCheckingCrops) {

      setLoginDisabled(false);

    }

  }, [storedToken, isCheckingCrops, hasCheckedCrops, failedCheckingCrops])

  useEffect(() => {
    
    let username: string;
    let token: string | null;

    if (hasLoggedIn && loginData || hasCheckedCrops) {
      
      if (hasCheckedCrops) {
      
        username = checkCropsData?.username || storedUsername;
        token = storedToken;

        navigate('/app-selection', { state: { username, token, completeRegistration: completeRegistration } });
        
      }

      if (loginData) {

        username = form.getFieldValue('username');
        clearAnalyticsFarmContext();
        localStorage.setItem(cookies.FarmersPlanToken, JSON.stringify({token: loginData?.token, username: username}));
        queryClient.removeQueries({ queryKey: ['getAccountInfo'] });
        void trackLoginSuccess(username);

        navigate('/app-selection', { state: { username, token: loginData?.token, completeRegistration: completeRegistration } });

      }
  
    }
    
  }, [hasLoggedIn, loginData, hasCheckedCrops, checkCropsData, storedUsername, storedToken]);

  useEffect(() => {

    const id = 'login';

    if (isLoggingIn) {

      message.loading({
        content: t('LoggingIn'),
        key: id,
      })

    }

    if (hasLoggedIn) {

      setLoginDisabled(true);
      message.success({
        content: t('SuccessLogin'),
        key: id,
      })

    }

    if (failedLoggingIn) {

      setLoginDisabled(false);

      const isRateLimitError = loginError instanceof ApiError
        && loginError.type === ERROR_TYPES.RATE_LIMITED;

      const errorContent = isRateLimitError
        ? getLoginRateLimitMessage(i18n.language || 'se')
        : loginError instanceof ApiError && loginError.message
          ? loginError.message
          : t('LoginFailedCheckPassword');

      message.error({
        content: errorContent,
        duration: isRateLimitError ? 10 : 8,
        key: id,
      });
    }

  }, [isLoggingIn, hasLoggedIn, failedLoggingIn, loginError, t]);

  
  const onFinish = (values: any) => {
    login(values);
  };

  const renderLoginForm = () => {

    return (
      <>
        <Col xs={24}>
          <Typography.Title level={1} style={{ textAlign: 'center' }}>
            {t('RegisterHere')}
          </Typography.Title>
          <div className="register-btn-wrapper">
            <a href="/register" className="register-btn-inner">
              {t('TryForFree')}
            </a>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 24, marginTop: 20, padding: '0 4px' }}>
            {([['ReadyInSeconds'], ['CompletelyFree']] as const).map(([key]) => (
              <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ color: '#52c41a', fontWeight: 700, fontSize: 16 }}>✓</span>
                <Typography.Text style={{ fontSize: 15, color: '#999' }}>{t(key)}</Typography.Text>
              </div>
            ))}
          </div>
        </Col>
        <Col
          xs={24}
          style={{
            marginTop: 10,
            borderTop: '1px solid #e8e8e8',
            paddingTop: 10,
          }}
        >
          <Typography.Title level={1} style={{ textAlign: 'center' }}>
            {t('Login')}
          </Typography.Title>
          <Form
            form={form}
            layout="vertical"
            style={{ fontSize: 18 }}
            onFinish={onFinish}
            validateTrigger="onChange"
          >
            <Form.Item
              // label={t('Email')}
              name="username"
              style={{
                fontSize: 18,
              }}
              rules={[
                {
                  required: true,
                  validator: async (_, value) => {

                    if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value)) {

                      return Promise.reject(new Error(`${t('RequireAValidEmail')}`));

                    }

                    if (!value) {

                      return Promise.reject(new Error(`${t('EmailIsRequired')}`));

                    }

                  },
                },
              ]}
            >
              <Input
                disabled={isLoggingIn}
                placeholder={t('Email')}
                size="large"
              />
            </Form.Item>
            <Form.Item
              // label={t('Password')}¨
              name="password"
              rules={[
                {
                  required: true,
                  message: t('PasswordIsRequired'),
                },
              ]}
            >
              <Input.Password
                disabled={isLoggingIn}
                placeholder={t('Password')}
                size="large"
                type="password"
                style={{
                  padding: '0px 10px 0px 0px',
                }}
              />
            </Form.Item>
            <Form.Item style={{ marginBottom: 4 }}>
              <Button
                disabled={isLoggingIn || loginDisabled}
                type="primary"
                size="large"
                style={{
                  width: '100%',
                  fontSize: 14,
                  backgroundColor: '#ff6b3d',
                  height: 46,
                }}
                htmlType="submit"
                // onClick={ async (e) => {

                //   e.preventDefault();

                //   form.submit();

                // }}
              >
                {t('Login')}
              </Button>
            </Form.Item>
          </Form>
        </Col>
        <Col
          xs={24}
          style={{
            textAlign: 'center',
            marginTop: 4,
            marginBottom: 16,
          }}
        >
          <Button
          size="large"
          style={{
            width: '100%',
            fontSize: 14,
            height: 46,
          }}
          href="https://farmersplan.com/prova-farmers-plan"
        >
          {t('BookFreeDemo')}
        </Button>
        </Col>
        <Col
          xs={24}
          style={{
            textAlign: 'center',
            marginTop: 4,
          }}
        >
          <Typography.Text style={{textAlign: 'center'}}><a href="/forgot">{t('ForgotPassword?')}</a></Typography.Text>
        </Col>
      </>
    );
    
  }

  const renderExistingSessionLoader = () => (
    <Col
      xs={24}
      style={{
        paddingTop: 120,
      }}
    >
      <div
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <Typography.Text style={{ color: '#00449f' }}>
          Laddar
        </Typography.Text>
        <Progress
          percent={sessionCheckProgress}
          showInfo={false}
          status="active"
          strokeColor="#00449f"
          trailColor="#f0f0f0"
          style={{ width: 260 }}
        />
      </div>
    </Col>
  );

  return (
    <>
      <Row
        style={{
          width: 320,
        }}
      >
        {isExistingSessionCheckPending ? renderExistingSessionLoader() : renderLoginForm()}
      </Row>
    </>
  )

}
